'use client';

import { useCallback, useRef, useState } from 'react';

import { getChaptersByWorkIdReq } from '@/features/fanfic-page/requests/chapters.requests';
import type { TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';

export interface UseReaderChaptersQueryReturn {
  readerChapters: TChapterWithoutContent[];
  isLoadingReaderChapters: boolean;
  error: Error | null;
  totalDocs: number;
  fetchAllReaderChapters: () => Promise<TChapterWithoutContent[]>;
  reset: () => void;
}

export interface UseReaderChaptersQueryProps {
  workId: string;
  chaptersCount: number;
}

/**
 * Hook for managing chapters list in the reader
 * Loads ALL chapters without content once and caches them
 * Independent from the chapters tab state
 */
export function useReaderChaptersQuery({
  workId,
  chaptersCount,
}: UseReaderChaptersQueryProps): UseReaderChaptersQueryReturn {
  const [readerChapters, setReaderChapters] = useState<TChapterWithoutContent[]>([]);
  const [isLoadingReaderChapters, setIsLoadingReaderChapters] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalDocs, setTotalDocs] = useState<number>(0);

  // Track if chapters have been loaded to prevent duplicate requests
  const hasLoadedRef = useRef<boolean>(false);

  const fetchAllReaderChapters = useCallback(async (): Promise<TChapterWithoutContent[]> => {
    // Return early if already loaded (caching)
    if (hasLoadedRef.current && readerChapters.length > 0) {
      return readerChapters;
    }

    setIsLoadingReaderChapters(true);
    setError(null);

    try {
      // Fetch all chapters without content in a single request
      const response = await getChaptersByWorkIdReq({
        workId,
        page: 1,
        limit: chaptersCount,
      });

      setReaderChapters(response.chapters);
      setTotalDocs(response.totalDocs);
      hasLoadedRef.current = true;
      return response.chapters;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch reader chapters'));
      console.error('Failed to fetch reader chapters:', err);
      return [];
    } finally {
      setIsLoadingReaderChapters(false);
    }
  }, [workId, readerChapters]);

  const reset = useCallback(() => {
    setReaderChapters([]);
    setTotalDocs(0);
    setError(null);
    hasLoadedRef.current = false;
  }, []);

  return {
    readerChapters,
    isLoadingReaderChapters,
    error,
    totalDocs,
    fetchAllReaderChapters,
    reset,
  };
}
